import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, NgOptimizedImage, PRECONNECT_CHECK_BLOCKLIST } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { EiqFilesModule } from '@eiq/files';
import { UtilsModule } from '@eiq/shared/utils';
import { UiAdminModule } from '@eiq/ui';

import { CommentsModule } from '@empathy/comments/feature';
import { SurveyCoreModule } from '@empathy/survey-core';
import { SurveySharedModule } from '@empathy/survey-shared';
import { EmpathyViewerModule } from '@empathy/survey-viewer';

import { BlockFormComponent } from './components/blocks/block-form/block-form.component';
import { BlockQuestionsComponent } from './components/blocks/block-form/block-questions/block-questions.component';
import { GroupBlockFormComponent } from './components/blocks/block-form/group-block-form/group-block-form.component';

import { SourceOptionsFormComponent } from './components/blocks/block-form/loop-block-form/custom-options-form/custom-options-form.component';
import { LoopBlockFormComponent } from './components/blocks/block-form/loop-block-form/loop-block-form.component';
import { ParentBlockFormComponent } from './components/blocks/block-form/parent-block-form/parent-block-form.component';
import { BlockTypePickerComponent } from './components/blocks/block-type-picker/block-type-picker.component';
import { BlocksQuotaComponent } from './components/blocks/blocks-quota/blocks-quota.component';
import { GroupBlockQuestionPickerComponent } from './components/blocks/group-block-question-picker/group-block-question-picker.component';
import { ListBlockItemComponent } from './components/blocks/list-block-item/list-block-item.component';
import { ListBlocksComponent } from './components/blocks/list-blocks/list-blocks.component';
import { LoopSettingsComponent } from './components/blocks/loop-settings/loop-settings.component';
import { LoopStimuliDialogComponent } from './components/blocks/loop-stimuli/loop-stimuli-dialog/loop-stimuli-dialog.component';
import { LoopStimuliComponent } from './components/blocks/loop-stimuli/loop-stimuli.component';
import { ConditionalBlocksLogicComponent } from './components/conditional-logic/conditional-blocks/conditional-blocks-logic.component';
import { ConditionValueViewComponent } from './components/conditional-logic/conditional-logic-form/condition-value-view/condition-value-view.component';
import { ConditionalLogicFormComponent } from './components/conditional-logic/conditional-logic-form/conditional-logic-form.component';
import { SelectedRangeViewComponent } from './components/conditional-logic/conditional-logic-form/selected-range-view/selected-range-view.component';
import { SelectedValueViewComponent } from './components/conditional-logic/conditional-logic-form/selected-value-view/selected-value-view.component';
import { ConditionalLogicComponent } from './components/conditional-logic/conditional-logic.component';
import { CarouselLogicFormComponent } from './components/conditional-logic/conditional-operator-forms/carousel-logic-form/carousel-logic-form.component';
import { ConditionalMultiSelectFormComponent } from './components/conditional-logic/conditional-operator-forms/conditional-multi-select-form/conditional-multi-select-form.component';
import { ConditionalNpsScoreFormComponent } from './components/conditional-logic/conditional-operator-forms/conditional-nps-score-form/conditional-nps-score-form.component';
import { ConditionalNumberFormComponent } from './components/conditional-logic/conditional-operator-forms/conditional-number-form/conditional-number-form.component';
import { ConditionalRangeFormComponent } from './components/conditional-logic/conditional-operator-forms/conditional-range-form/conditional-range-form.component';
import { ConditionalSingleChoiceFormComponent } from './components/conditional-logic/conditional-operator-forms/conditional-single-choice-form/conditional-single-choice-form.component';
import { ConditionalTextFormComponent } from './components/conditional-logic/conditional-operator-forms/conditional-text-form/conditional-text-form.component';
import { LogicOperatorDropdownComponent } from './components/conditional-logic/conditional-operator-forms/logic-operator-dropdown/logic-operator-dropdown.component';
import { QuestionOptionsDropdownComponent } from './components/conditional-logic/conditional-operator-forms/question-options-dropdown/question-options-dropdown.component';
import { ConditionsBlockComponent } from './components/conditional-logic/conditions-block/conditions-block.component';
import { ConditionsSavedComponent } from './components/conditional-logic/conditions-saved/conditions-saved.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { DeviceWrapperComponent } from './components/device-wrapper/device-wrapper.component';
import { ChangeQuestionTypeDialogComponent } from './components/dialogs/change-question-type-dialog/change-question-type-dialog.component';
import { DeleteQuestionDialogComponent } from './components/dialogs/delete-question-dialog/delete-question-dialog.component';
import { DuplicateQuestionsDialogComponent } from './components/dialogs/duplicate-questions-dialog/duplicate-questions-dialog.component';
import { EndPageFormComponent } from './components/end-page-form/end-page-form.component';
import { EndPagePreviewComponent } from './components/end-page-preview/end-page-preview.component';
import { IconTooltipComponent } from './components/icon-tooltip/icon-tooltip.component';
import { ListBlockQuestionsItemComponent } from './components/list/list-block-item/list-block-item.component';
import { ListEndPageItemComponent } from './components/list/list-end-page-item/list-end-page-item.component';
import { ListItemButtonComponent } from './components/list/list-item-button/list-item-button.component';
import { ListQuestionItemComponent } from './components/list/list-question-item/list-question-item.component';
import { ListQuestionWrapperComponent } from './components/list/list-question-item-wrapper/list-question-item-wrapper.component';
import { ListQuestionsComponent } from './components/list/list-questions/list-questions.component';
import { ListVariableItemComponent } from './components/list/list-variable-item/list-variable-item.component';
import { ListWelcomeItemComponent } from './components/list/list-welcome-item/list-welcome-item.component';
import { ListQuestionsHiddenComponent } from './components/list-questions-hidden/list-questions-hidden.component';
import { MainTabsComponent } from './components/main-tabs/main-tabs.component';
import { MediaViewComponent } from './components/media-view/media-view.component';
import { PreviewComponent } from './components/preview/preview.component';
import { PreviewViewerHostComponent } from './components/preview-viewer-host/preview-viewer-host.component';
import { PreviewWrapperComponent } from './components/preview-wrapper/preview-wrapper.component';
import { ProgrammerNotesDialogComponent } from './components/programmer-notes/programmer-notes-dialog/programmer-notes-dialog.component';
import { ProgrammerNotesComponent } from './components/programmer-notes/programmer-notes.component';
import { QuestionFormComponent } from './components/question-form/question-form.component';
import { QuestionLibraryItemComponent } from './components/question-library/question-library-item/question-library-item.component';
import { QuestionLibraryComponent } from './components/question-library/question-library.component';
import { QuestionPipePickerComponent } from './components/question-pipe-picker/question-pipe-picker.component';
import { JumpToPositionComponent } from './components/question-position/jump-to-position/jump-to-position.component';
import { MoveBulkToPositionComponent } from './components/question-position/move-bulk-to-position/move-bulk-to-position.component';
import { PositioningActionsComponent } from './components/question-position/positioning-actions/positioning-actions.component';
import { QuestionPreviewComponent } from './components/question-preview/question-preview.component';
import { CardsSettingsComponent } from './components/question-settings/cards-settings/cards-settings.component';
import { CopyCardsConfirmationComponent } from './components/question-settings/cards-settings/copy-cards-confirmation/copy-cards-confirmation.component';
import { ChoiceSettingsComponent } from './components/question-settings/choice-settings/choice-settings.component';
import { CopyChoicesConfirmationComponent } from './components/question-settings/choice-settings/copy-choices-confirmation/copy-choices-confirmation.component';
import { CopyHiddenChoicesConfirmationComponent } from './components/question-settings/hidden-choice-settings/copy-hidden-choices-confirmation/copy-hidden-choices-confirmation.component';
import { HiddenChoiceSettingsComponent } from './components/question-settings/hidden-choice-settings/hidden-choice-settings.component';
import { MediaSettingsComponent } from './components/question-settings/media-settings/media-settings.component';
import { MinViewTimeSettingComponent } from './components/question-settings/min-view-time-setting/min-view-time-setting.component';
import { QuestionSettingsComponent } from './components/question-settings/question-settings.component';
import { AIChatSettingsComponent } from './components/question-settings/question-type-settings-components/ai-chat-settings/ai-chat-settings.component';
import { CarouselSettingsComponent } from './components/question-settings/question-type-settings-components/carousel-settings/carousel-settings.component';
import { DropdownSettingsComponent } from './components/question-settings/question-type-settings-components/dropdown-choice-settings/dropdown-settings.component';
import { EssaySettingsComponent } from './components/question-settings/question-type-settings-components/essay-settings/essay-settings.component';
import { FileUploadSettingsComponent } from './components/question-settings/question-type-settings-components/file-upload-settings/file-upload-settings.component';
import { HiddenQuestionSettingsComponent } from './components/question-settings/question-type-settings-components/hidden-question-settings/hidden-question-settings.component';
import { InfoPageSettingsComponent } from './components/question-settings/question-type-settings-components/info-page-settings/info-page-settings.component';
import { InputSettingsComponent } from './components/question-settings/question-type-settings-components/input-settings/input-settings.component';
import { MultiChoiceSettingsComponent } from './components/question-settings/question-type-settings-components/multi-choice-settings/multi-choice-settings.component';
import { RankingSettingsComponent } from './components/question-settings/question-type-settings-components/ranking-settings/ranking-settings.component';
import { RatingSettingsComponent } from './components/question-settings/question-type-settings-components/rating-settings/rating-settings.component';
import { SingleChoiceSettingsComponent } from './components/question-settings/question-type-settings-components/single-choice-settings/single-choice-settings.component';
import { RandomizedOptionsSettingComponent } from './components/question-settings/randomized-options-setting/randomized-options-setting.component';
import { RandomizedQuestionsSettingComponent } from './components/question-settings/randomized-questions-setting/randomized-questions-setting.component';
import { RequiredSettingComponent } from './components/question-settings/required-setting/required-setting.component';
import { AIChatDescriptionComponent } from './components/question-type-description-components/ai-chat/ai-chat-description.component';
import { CarouselDescriptionComponent } from './components/question-type-description-components/carousel/carousel-description.component';
import { CarouselRatingDescriptionComponent } from './components/question-type-description-components/carousel-rating/carousel-rating-description.component';
import { DropdownDescriptionComponent } from './components/question-type-description-components/dropdown/dropdown-description.component';
import { EssayDescriptionComponent } from './components/question-type-description-components/essay/essay-description.component';
import { FileUploadDescriptionComponent } from './components/question-type-description-components/file-upload/file-upload-description.component';
import { HeatmapDescriptionComponent } from './components/question-type-description-components/heatmap/heatmap-description.component';
import { InputDescriptionComponent } from './components/question-type-description-components/input/input-description.component';
import { MultiChoiceDescriptionComponent } from './components/question-type-description-components/multi-choice/multi-choice-description.component';
import { NPSDescriptionComponent } from './components/question-type-description-components/nps/nps-description.component';
import { RankSortDescriptionComponent } from './components/question-type-description-components/rank-sort/rank-sort-description.component';
import { RankingDescriptionComponent } from './components/question-type-description-components/ranking/ranking-description.component';
import { RatingDescriptionComponent } from './components/question-type-description-components/rating/rating-description.component';
import { SingleChoiceDescriptionComponent } from './components/question-type-description-components/single-choice/single-choice-description.component';
import { TerminateDescriptionComponent } from './components/question-type-description-components/terminate/terminate-description.component';
import { AIChatFormComponent } from './components/question-type-form-components/ai-chat-form/ai-chat-form.component';
import { CardsFormComponent } from './components/question-type-form-components/cards-form/cards-form.component';
import { CarouselFormComponent } from './components/question-type-form-components/carousel-form/carousel-form.component';
import { CarouselRatingFormComponent } from './components/question-type-form-components/carousel-rating-form/carousel-rating-form.component';
import { ChoicesFormComponent } from './components/question-type-form-components/choices-form/choices-form.component';
import { FileUploadFormComponent } from './components/question-type-form-components/file-upload-form/file-upload-form.component';
import { HeatmapFormComponent } from './components/question-type-form-components/heatmap-form/heatmap-form.component';
import { HiddenChoicesFormComponent } from './components/question-type-form-components/hidden-choices-form/hidden-choices-form.component';
import { InfoFormComponent } from './components/question-type-form-components/info-form/info-form.component';
import { NPSFormComponent } from './components/question-type-form-components/nps-form/nps-form.component';
import { QuestionTermsFormComponent } from './components/question-type-form-components/question-terms-form/question-terms-form.component';
import { RankSortFormComponent } from './components/question-type-form-components/rank-sort-form/rank-sort-form.component';
import { RankingFormComponent } from './components/question-type-form-components/ranking-form/ranking-form.component';
import { RatingValuesFormComponent } from './components/question-type-form-components/rating-values-form/rating-values-form.component';
import { QuestionTypePickerComponent } from './components/question-type-picker/question-type-picker.component';
import { QuestionTypeVideoComponent } from './components/question-type-picker/question-type-video/question-type-video.component';
import { ImportCsvDialogComponent } from './components/settings-form/import-csv-dialog/import-csv-dialog.component';
import { SettingsFormComponent } from './components/settings-form/settings-form.component';
import { FlagsFormTermItemComponent } from './components/settings-form/term-item/term-item.component';
import { TermsFormComponent } from './components/settings-form/terms-form/terms-form.component';
import { ListSourceItemComponent } from './components/sources-configuration/list-source-item/list-source-item.component';
import { ListSourcesComponent } from './components/sources-configuration/list-sources/list-sources.component';
import { PreviewVariablesFormComponent } from './components/sources-configuration/preview-variables-form/preview-variables-form.component';
import { SourceFormComponent } from './components/sources-configuration/source-form/source-form.component';
import { SourcePickerComponent } from './components/sources-configuration/source-picker/source-picker.component';
import { SourceVariableDialogComponent } from './components/sources-configuration/source-variable-dialog/source-variable-dialog.component';
import { VariableChoicesFormComponent } from './components/sources-configuration/variable-form/variable-choices-form/variable-choices-form.component';
import { VariableFormComponent } from './components/sources-configuration/variable-form/variable-form.component';
import { StimulusPickerDialogComponent } from './components/stimulus/stimulus-picker/stimulus-picker-dialog.component';
import { SurveyFullPreviewComponent } from './components/survey-full-preview/survey-full-preview.component';
import { TextEditorComponent } from './components/text-editor/text-editor.component';
import { TranslationsLanguageToggleComponent } from './components/translations/translations-language-toggle/translations-language-toggle.component';
import { TranslationsPreviewComponent } from './components/translations/translations-preview/translations-preview.component';
import { TranslationsQuestionsListComponent } from './components/translations/translations-questions-list/translations-questions-list.component';
import { TranslationsComponent } from './components/translations/translations.component';
import { WelcomePageFormComponent } from './components/welcome-page-form/welcome-page-form.component';
import { WelcomePagePreviewComponent } from './components/welcome-page-preview/welcome-page-preview.component';
import { EditorComponent } from './containers/editor/editor.component';
import { PositionRelativeDirective } from './directives/block-box.directive';
import { FaIconColorDirective } from './directives/fa-icon-color.directive';
import { InfiniteScrollDirective } from './directives/infinite-scroll.directive';
import { TrimSpecialCharactersDirective } from './directives/trim-text.directive';
import { BlockPipe } from './pipes/block.pipe';
import { CardTextPipe } from './pipes/card-text.pipe';
import { InBlockPipe } from './pipes/in-block.pipe';
import { OperatorByTypePipe } from './pipes/operator-by-type.pipe';
import { OptionTextByTypePipe } from './pipes/option-text-by-type.pipe';
import { OptionTextOrDefaultPipe } from './pipes/option-text-or-default.pipe';
import { QuestionCodePipe } from './pipes/question-code.pipe';

@NgModule({
  imports: [
    CommonModule,
    CKEditorModule,
    CommentsModule,
    DragDropModule,
    EiqFilesModule,
    EmpathyViewerModule,
    FontAwesomeModule,
    FormsModule,
    ListItemButtonComponent,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatTableModule,
    MatPaginatorModule,
    MatChipsModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MediaSettingsComponent,
    MinViewTimeSettingComponent,
    NgOptimizedImage,
    ReactiveFormsModule,
    SurveyCoreModule,
    SurveySharedModule,
    UiAdminModule,
    UtilsModule,
    TranslationsComponent
  ],
  declarations: [
    LoopSettingsComponent,
    LoopStimuliComponent,
    LoopStimuliDialogComponent,
    AIChatDescriptionComponent,
    AIChatFormComponent,
    BlockFormComponent,
    BlockQuestionsComponent,
    BlocksQuotaComponent,
    BlockTypePickerComponent,
    CardsFormComponent,
    CarouselDescriptionComponent,
    CarouselFormComponent,
    AIChatSettingsComponent,
    CarouselLogicFormComponent,
    CarouselRatingDescriptionComponent,
    CarouselRatingFormComponent,
    CarouselSettingsComponent,
    CardsSettingsComponent,
    ChoiceSettingsComponent,
    ChoicesFormComponent,
    ConditionValueViewComponent,
    ConditionalBlocksLogicComponent,
    ConditionalLogicComponent,
    ConditionalLogicFormComponent,
    ConditionalMultiSelectFormComponent,
    ConditionalNpsScoreFormComponent,
    ConditionalNumberFormComponent,
    ConditionalRangeFormComponent,
    ConditionalSingleChoiceFormComponent,
    ConditionalTextFormComponent,
    ConditionsBlockComponent,
    ConditionsSavedComponent,
    ConfirmDialogComponent,
    CopyCardsConfirmationComponent,
    CopyChoicesConfirmationComponent,
    CopyHiddenChoicesConfirmationComponent,
    ChangeQuestionTypeDialogComponent,
    DeleteQuestionDialogComponent,
    DuplicateQuestionsDialogComponent,
    DeviceWrapperComponent,
    DropdownDescriptionComponent,
    DropdownSettingsComponent,
    EditorComponent,
    EndPageFormComponent,
    EndPagePreviewComponent,
    EssayDescriptionComponent,
    EssaySettingsComponent,
    FileUploadDescriptionComponent,
    FileUploadFormComponent,
    FileUploadSettingsComponent,
    FlagsFormTermItemComponent,
    GroupBlockFormComponent,
    GroupBlockQuestionPickerComponent,
    HeatmapDescriptionComponent,
    HeatmapFormComponent,
    HiddenChoicesFormComponent,
    HiddenChoiceSettingsComponent,
    HiddenQuestionSettingsComponent,
    IconTooltipComponent,
    InfoPageSettingsComponent,
    ImportCsvDialogComponent,
    InfoFormComponent,
    InputDescriptionComponent,
    InputSettingsComponent,
    JumpToPositionComponent,
    ListBlockItemComponent,
    ListBlockQuestionsItemComponent,
    ListBlocksComponent,
    ListEndPageItemComponent,
    ListQuestionsComponent,
    ListQuestionsHiddenComponent,
    ListQuestionItemComponent,
    ListQuestionWrapperComponent,
    ListSourceItemComponent,
    ListSourcesComponent,
    ListVariableItemComponent,
    ListWelcomeItemComponent,
    LoopBlockFormComponent,
    LogicOperatorDropdownComponent,
    MainTabsComponent,
    MediaViewComponent,
    MoveBulkToPositionComponent,
    MultiChoiceDescriptionComponent,
    MultiChoiceSettingsComponent,
    NPSDescriptionComponent,
    NPSFormComponent,
    ParentBlockFormComponent,
    PreviewVariablesFormComponent,
    PositioningActionsComponent,
    PreviewWrapperComponent,
    PreviewComponent,
    PreviewViewerHostComponent,
    ProgrammerNotesComponent,
    ProgrammerNotesDialogComponent,
    QuestionOptionsDropdownComponent,
    QuestionFormComponent,
    QuestionLibraryComponent,
    QuestionLibraryItemComponent,
    QuestionPipePickerComponent,
    QuestionPreviewComponent,
    QuestionSettingsComponent,
    QuestionTermsFormComponent,
    QuestionTypePickerComponent,
    QuestionTypeVideoComponent,
    RandomizedOptionsSettingComponent,
    RandomizedQuestionsSettingComponent,
    RatingDescriptionComponent,
    RatingSettingsComponent,
    RatingValuesFormComponent,
    RankSortDescriptionComponent,
    RankingDescriptionComponent,
    RankSortFormComponent,
    RankingFormComponent,
    RankingSettingsComponent,
    RequiredSettingComponent,
    SelectedValueViewComponent,
    SelectedRangeViewComponent,
    SettingsFormComponent,
    SingleChoiceDescriptionComponent,
    SingleChoiceSettingsComponent,
    SourceFormComponent,
    SourceOptionsFormComponent,
    SourcePickerComponent,
    SourceVariableDialogComponent,
    SurveyFullPreviewComponent,
    StimulusPickerDialogComponent,
    TerminateDescriptionComponent,
    TermsFormComponent,
    TextEditorComponent,
    TranslationsLanguageToggleComponent,
    TranslationsPreviewComponent,
    TranslationsQuestionsListComponent,
    VariableChoicesFormComponent,
    VariableFormComponent,
    WelcomePageFormComponent,
    WelcomePagePreviewComponent,
    FaIconColorDirective,
    InfiniteScrollDirective,
    TrimSpecialCharactersDirective,
    PositionRelativeDirective,
    BlockPipe,
    CardTextPipe,
    InBlockPipe,
    OperatorByTypePipe,
    OptionTextByTypePipe,
    OptionTextOrDefaultPipe,
    QuestionCodePipe
  ],
  providers: [{ provide: PRECONNECT_CHECK_BLOCKLIST, useValue: 'https://mompublicimages.blob.core.windows.net' }],
  exports: [EditorComponent, DeviceWrapperComponent, MediaViewComponent]
})
export class EmpathyEditorModule {}
